import { useId, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { Building2, FileText, Mail, Phone } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
  Skeleton,
  toast,
} from '@helios/ui';
import { PERMISSIONS } from '@helios/authz';
import { sessionScopeKey, useSessionScopeFence } from '@/hooks/useSessionScope';
import { useScopedNumericQuery } from '@/hooks/useScopedNumericQuery';
import { apiClient } from '@/services/apiClient';
import { useProjectDetail } from '@/features/projects/projects.hooks';
import type { M365ConnectionStatus } from '@/features/m365-workspace/m365Workspace.types';
import type { FinanceInvoiceRecord, InvoiceDirection } from './financeInvoices.types';
import { FinanceInvoiceFollowups } from './FinanceInvoiceFollowups';
import { invoiceDisplayDate } from './financeInvoiceInsights';
import { financeInvoiceEvidenceApi } from './financeInvoiceEvidence.api';
import {
  invoiceEvidenceRead,
  useFinanceInvoiceActivityOptions,
  useFinanceInvoiceEvidence,
} from './financeInvoiceEvidence.hooks';
import type {
  InvoiceActivitySource,
  InvoiceCommunicationEvidence,
} from './financeInvoiceEvidence.types';
import { paginateEvidenceText } from './financeInvoiceEvidenceText';
import './finance-invoice-evidence.css';

type WorkspaceTab = 'activity' | 'followups' | 'job';
const tabs = [
  { id: 'activity', label: 'Activity' },
  { id: 'followups', label: 'Follow-ups' },
  { id: 'job', label: 'Job info' },
] as const;
const manageGrant = PERMISSIONS.FINANCE_INVOICE_COMMUNICATIONS_MANAGE;

export function FinanceInvoiceEvidenceWorkspace(props: {
  invoice: FinanceInvoiceRecord;
  direction: InvoiceDirection;
  asOf: string;
}) {
  const access = useSessionScopeFence(invoiceEvidenceRead(props.direction));
  if (!access.enabled)
    return (
      <p className="text-muted-foreground text-xs">
        Invoice activity requires current invoice read access.
      </p>
    );
  return (
    <EvidenceWorkspace
      key={JSON.stringify([sessionScopeKey(access.identity), props.direction, props.invoice.id])}
      {...props}
    />
  );
}

function EvidenceWorkspace({
  invoice,
  direction,
  asOf,
}: {
  invoice: FinanceInvoiceRecord;
  direction: InvoiceDirection;
  asOf: string;
}) {
  const [tab, setTab] = useState<WorkspaceTab>('activity');
  const prefix = useId();
  const buttons = useRef<Array<HTMLButtonElement | null>>([]);
  return (
    <section className="invoice-evidence" aria-label="Invoice communications and job workspace">
      <div className="invoice-evidence__tabs" role="tablist" aria-label="Invoice evidence views">
        {tabs.map((item, index) => (
          <button
            key={item.id}
            ref={(node) => {
              buttons.current[index] = node;
            }}
            id={`${prefix}-${item.id}`}
            type="button"
            role="tab"
            aria-selected={tab === item.id}
            aria-controls={`${prefix}-panel`}
            tabIndex={tab === item.id ? 0 : -1}
            onClick={() => setTab(item.id)}
            onKeyDown={(event) => {
              const next =
                event.key === 'ArrowRight'
                  ? (index + 1) % tabs.length
                  : event.key === 'ArrowLeft'
                    ? (index + tabs.length - 1) % tabs.length
                    : event.key === 'Home'
                      ? 0
                      : event.key === 'End'
                        ? tabs.length - 1
                        : null;
              if (next === null) return;
              event.preventDefault();
              setTab(tabs[next]!.id);
              buttons.current[next]?.focus();
            }}
          >
            {item.label}
          </button>
        ))}
      </div>
      <div
        className="invoice-evidence__panel"
        id={`${prefix}-panel`}
        role="tabpanel"
        aria-labelledby={`${prefix}-${tab}`}
      >
        {tab === 'activity' ? (
          <InvoiceActivity invoice={invoice} direction={direction} />
        ) : tab === 'followups' ? (
          <FinanceInvoiceFollowups direction={direction} id={invoice.id} compact />
        ) : (
          <InvoiceJobInfo invoice={invoice} asOf={asOf} />
        )}
      </div>
    </section>
  );
}

function InvoiceActivity({
  invoice,
  direction,
}: {
  invoice: FinanceInvoiceRecord;
  direction: InvoiceDirection;
}) {
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<InvoiceCommunicationEvidence | null>(null);
  const [linking, setLinking] = useState(false);
  const query = useFinanceInvoiceEvidence(direction, invoice.id, page);
  const manage = useSessionScopeFence(manageGrant);
  const current =
    !query.isFetching && !query.isError && query.data?.invoiceId === invoice.id
      ? query.data
      : undefined;
  return (
    <div className="invoice-evidence__activity">
      <header className="invoice-evidence__heading">
        <div>
          <h3>Communication history</h3>
          <p>Original messages, replies & recorded notes</p>
        </div>
        {manage.enabled && current?.permissions?.canLink ? (
          <Button size="xs" variant="outline" onClick={() => setLinking(true)}>
            Link activity
          </Button>
        ) : null}
      </header>
      {query.isLoading || query.isFetching ? (
        <Skeleton className="h-44 w-full" aria-label="Loading invoice communications" />
      ) : query.isError || !current ? (
        <div className="invoice-evidence__empty" role="alert">
          <Mail size={22} />
          <strong>Communication records unavailable</strong>
          <p>Refresh to inspect the current authorized source.</p>
          <Button size="xs" variant="outline" onClick={() => void query.refetch()}>
            Retry invoice activity
          </Button>
        </div>
      ) : (
        <>
          {current.provider ? (
            <div className="invoice-evidence__provider">
              <span>
                {current.provider.source === 'quickbooks_online'
                  ? 'QuickBooks Online'
                  : current.provider.source}{' '}
                email status
              </span>
              <strong>{current.provider.emailStatus ?? 'Not recorded'}</strong>
              <p
                title={`Source observed ${new Date(current.provider.observedAt).toLocaleString()}; ${current.provider.sentAt ? `recorded send ${new Date(current.provider.sentAt).toLocaleString()}` : 'send timestamp not recorded'}`}
              >
                {current.provider.sentAt
                  ? `Sent ${new Date(current.provider.sentAt).toLocaleDateString()}`
                  : 'Send timestamp not recorded'}{' '}
                · source observed {new Date(current.provider.observedAt).toLocaleDateString()}
              </p>
            </div>
          ) : null}
          <div className="invoice-evidence__records">
            {current.items.length ? (
              current.items.map((item) => (
                <article key={item.id} className="invoice-evidence__record">
                  <div className="invoice-evidence__record-type">
                    {item.kind === 'CALL' ? (
                      <Phone size={14} />
                    ) : item.kind === 'NOTE' ? (
                      <FileText size={14} />
                    ) : (
                      <Mail size={14} />
                    )}
                    <span>{activityLabel(item)}</span>
                    <time dateTime={item.sentAt ?? item.occurredAt ?? item.recordedAt}>
                      {new Date(
                        item.sentAt ?? item.occurredAt ?? item.recordedAt,
                      ).toLocaleDateString(undefined, {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </time>
                  </div>
                  <h4>{item.subject || 'Untitled recorded activity'}</h4>
                  <p className="invoice-evidence__preview">
                    {item.bodyText || 'Message body not recorded.'}
                  </p>
                  <p className="invoice-evidence__source">
                    {item.fromEmail ?? item.actorName ?? 'ConstructionOS record'}
                    {item.status ? ` · ${item.status}` : ''}
                  </p>
                  <Button
                    size="xs"
                    variant="outline"
                    className="w-full"
                    onClick={() => setSelected(item)}
                  >
                    Read full {item.kind === 'CALL' || item.kind === 'NOTE' ? 'note' : 'message'}
                  </Button>
                </article>
              ))
            ) : (
              <div className="invoice-evidence__empty">
                <Mail size={23} />
                <strong>
                  {current.coverage.email === 'restricted'
                    ? 'Email source access restricted'
                    : 'No invoice-linked messages recorded'}
                </strong>
                <p>
                  {current.coverage.email === 'restricted'
                    ? 'Invoice access does not grant access to mailbox messages.'
                    : current.provider
                      ? 'Provider email status does not include the original message body. Link the exact conversation to inspect it here.'
                      : 'Link an existing conversation, call or note to keep its original content with this invoice.'}
                </p>
              </div>
            )}
          </div>
          <div className="invoice-evidence__paging">
            <span>
              {current.meta.total} authorized record{current.meta.total === 1 ? '' : 's'} ·{' '}
              {current.meta.page}/{Math.max(1, current.meta.totalPages)}
            </span>
            <div>
              <Button
                size="xs"
                variant="ghost"
                aria-label="Previous invoice activity"
                disabled={page <= 1}
                onClick={() => setPage((value) => value - 1)}
              >
                Previous
              </Button>
              <Button
                size="xs"
                variant="ghost"
                aria-label="Next invoice activity"
                disabled={page >= current.meta.totalPages}
                onClick={() => setPage((value) => value + 1)}
              >
                Next
              </Button>
            </div>
          </div>
          <p className="invoice-evidence__boundary">
            {current.coverage.calls === 'restricted' || current.coverage.notes === 'restricted'
              ? 'Some call or note sources require additional access. '
              : ''}
            Only explicitly linked records appear. Send status does not prove delivery.
          </p>
        </>
      )}
      <MailboxCoverage />
      <Dialog
        open={Boolean(selected)}
        onOpenChange={(open) => {
          if (!open) setSelected(null);
        }}
      >
        <DialogContent className="invoice-evidence-reader">
          {selected ? (
            <EvidenceReader
              key={`${selected.id}:${selected.recordedAt}`}
              item={selected}
              direction={direction}
              invoiceId={invoice.id}
              canUnlink={manage.enabled && Boolean(selected.linkId)}
              onUnlinked={() => {
                setSelected(null);
                setPage(1);
                void query.refetch();
              }}
            />
          ) : null}
        </DialogContent>
      </Dialog>
      <Dialog open={linking} onOpenChange={setLinking}>
        <DialogContent className="invoice-evidence-picker">
          {linking ? (
            <ActivityPicker
              invoiceId={invoice.id}
              direction={direction}
              onLinked={() => {
                setLinking(false);
                setPage(1);
                void query.refetch();
              }}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MailboxCoverage() {
  const access = useSessionScopeFence(PERMISSIONS.M365_WORKSPACE_READ);
  return access.enabled ? (
    <MailboxStatus />
  ) : (
    <p className="invoice-evidence__coverage">
      Mailbox coverage unavailable with current access. No absence of correspondence is inferred.
    </p>
  );
}
function MailboxStatus() {
  const query = useScopedNumericQuery(
    ['finance', 'invoice-mailbox-status'],
    PERMISSIONS.M365_WORKSPACE_READ,
    async (options) => {
      const response = await apiClient.get<M365ConnectionStatus>('/m365-workspace/status', options);
      if (!response.ok || !response.data) throw new Error('Mailbox status unavailable');
      return response.data;
    },
    { staleTime: 20000 },
  );
  const status = !query.isFetching && !query.isError ? query.data : undefined;
  return (
    <p className="invoice-evidence__coverage">
      {query.isLoading || query.isFetching
        ? 'Checking mailbox coverage…'
        : !status
          ? 'Mailbox connection could not be verified.'
          : status.requiresReconnect || Boolean(status.missingScopes?.length)
            ? 'Mailbox consent incomplete; reconnect is required to verify current correspondence.'
            : status.state === 'CONNECTED'
              ? 'Personal mailbox connected; shared mailboxes and unlinked conversations are outside this history.'
              : 'Mailbox connection incomplete; this history does not prove no email was sent.'}
    </p>
  );
}

function InvoiceJobInfo({ invoice, asOf }: { invoice: FinanceInvoiceRecord; asOf: string }) {
  const access = useSessionScopeFence(PERMISSIONS.PROJECTS_READ);
  if (!invoice.project)
    return (
      <div className="invoice-evidence__empty">
        <Building2 size={24} />
        <strong>No accessible job linked</strong>
        <p>This invoice has no recorded project association available to your current access.</p>
      </div>
    );
  if (!access.enabled)
    return (
      <div className="invoice-evidence__empty">
        <Building2 size={24} />
        <strong>Job information restricted</strong>
        <p>Project read access is required to inspect the associated job.</p>
      </div>
    );
  return <JobDetail projectId={invoice.project.id} asOf={asOf} />;
}
function JobDetail({ projectId, asOf }: { projectId: string; asOf: string }) {
  const query = useProjectDetail(projectId);
  const job =
    !query.isFetching && !query.isError && query.data?.id === projectId ? query.data : undefined;
  if (query.isLoading || query.isFetching)
    return <Skeleton className="h-52 w-full" aria-label="Loading associated job" />;
  if (!job)
    return (
      <div className="invoice-evidence__empty" role="alert">
        <Building2 size={24} />
        <strong>Job source unavailable</strong>
        <p>Refresh to load the current job record.</p>
        <Button size="xs" variant="outline" onClick={() => void query.refetch()}>
          Retry job information
        </Button>
      </div>
    );
  const fields = [
    ['Client', job.clientName ?? 'Not recorded'],
    ['Phase', job.phase.replaceAll('_', ' ')],
    ['Sector', job.sector ?? 'Not recorded'],
    [
      'Location',
      [job.location.city, job.location.state].filter(Boolean).join(', ') || 'Not recorded',
    ],
    ['Start', invoiceDisplayDate(job.schedule.startDate?.slice(0, 10) ?? null)],
    ['Completion', invoiceDisplayDate(job.schedule.substantialCompletion?.slice(0, 10) ?? null)],
  ];
  return (
    <div className="invoice-evidence__job">
      <header>
        <span>{job.projectNumber}</span>
        <h3>{job.name}</h3>
        <p>
          {job.status.replaceAll('_', ' ')} · {job.riskLevel} recorded risk
        </p>
      </header>
      <dl>
        {fields.map(([label, value]) => (
          <div key={label}>
            <dt>{label}</dt>
            <dd title={value}>{value}</dd>
          </div>
        ))}
      </dl>
      <p className="invoice-evidence__boundary">
        Current job record · invoice aging date {invoiceDisplayDate(asOf)}
      </p>
      <Link
        className="invoice-evidence__job-link"
        to={`/projects/overview?projectId=${encodeURIComponent(job.id)}`}
      >
        <Building2 size={14} />
        Open full job workspace
      </Link>
      <p className="invoice-evidence__coverage">
        Schedule, budget, documents, team and project controls remain in the native job workspace.
      </p>
    </div>
  );
}

function activityLabel(item: InvoiceCommunicationEvidence): string {
  if (item.kind === 'CALL') return 'Recorded phone call';
  if (item.kind === 'NOTE') return 'Recorded note';
  if (item.kind === 'THREAD_MESSAGE')
    return item.direction === 'INBOUND' ? 'Recorded inbound email' : 'Recorded conversation';
  return item.transport === 'log' || item.status === 'SIMULATED'
    ? 'Recorded email attempt'
    : 'Recorded outbound email';
}

function EvidenceReader({
  item,
  direction,
  invoiceId,
  canUnlink,
  onUnlinked,
}: {
  item: InvoiceCommunicationEvidence;
  direction: InvoiceDirection;
  invoiceId: string;
  canUnlink: boolean;
  onUnlinked: () => void;
}) {
  const [page, setPage] = useState(0);
  const [box, setBox] = useState({ columns: 70, rows: 15 });
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const body = useRef<HTMLDivElement>(null);
  const access = useSessionScopeFence(manageGrant);
  useLayoutEffect(() => {
    const target = body.current;
    if (!target) return;
    const measure = () => {
      if (target.clientWidth && target.clientHeight)
        setBox({
          columns: Math.max(12, Math.floor((target.clientWidth - 24) / 7.2) - 2),
          rows: Math.max(2, Math.floor((target.clientHeight - 24) / 18) - 1),
        });
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(target);
    return () => observer.disconnect();
  }, []);
  const text = [
    `Subject: ${item.subject}`,
    item.fromEmail
      ? `From: ${item.fromEmail}`
      : item.actorName
        ? `Recorded by: ${item.actorName}`
        : '',
    item.toEmails.length ? `To: ${item.toEmails.join(', ')}` : '',
    item.ccEmails.length ? `Cc: ${item.ccEmails.join(', ')}` : '',
    `Recorded: ${new Date(item.recordedAt).toLocaleString()}`,
    item.occurredAt ? `Occurred: ${new Date(item.occurredAt).toLocaleString()}` : '',
    item.sentAt ? `Send timestamp: ${new Date(item.sentAt).toLocaleString()}` : '',
    item.status ? `Status: ${item.status}` : '',
    item.transport ? `Transport: ${item.transport}` : '',
    '',
    item.bodyText || 'Message body not recorded.',
  ]
    .filter((value, index) => Boolean(value) || index > 7)
    .join('\n');
  const pages = paginateEvidenceText(text, box.columns, box.rows);
  const currentPage = Math.min(page, pages.length - 1);
  return (
    <>
      <DialogHeader>
        <DialogTitle>{activityLabel(item)}</DialogTitle>
        <DialogDescription>
          Original recorded text · send status does not prove recipient delivery.
        </DialogDescription>
      </DialogHeader>
      <div className="invoice-evidence-reader__body" ref={body}>
        <pre aria-label="Original recorded communication text">{pages[currentPage]}</pre>
      </div>
      <footer className="invoice-evidence-reader__footer">
        <span>
          Text page {currentPage + 1} of {pages.length}
        </span>
        <div>
          <Button
            size="xs"
            variant="ghost"
            aria-label="Previous message text page"
            disabled={currentPage === 0}
            onClick={() => setPage(currentPage - 1)}
          >
            Previous
          </Button>
          <Button
            size="xs"
            variant="ghost"
            aria-label="Next message text page"
            disabled={currentPage >= pages.length - 1}
            onClick={() => setPage(currentPage + 1)}
          >
            Next
          </Button>
        </div>
        {item.href ? (
          <Link to={item.href} className="text-xs underline">
            Open source
          </Link>
        ) : null}
        {canUnlink && item.linkId ? (
          <Button
            size="xs"
            variant="outline"
            disabled={pending}
            onClick={async () => {
              if (!access.enabled || !access.isCurrent(access.identity)) return;
              setPending(true);
              setError('');
              try {
                await financeInvoiceEvidenceApi.unlink(direction, invoiceId, item.linkId!, {
                  sessionOrigin: access.identity.sessionOrigin,
                  allowRefresh: false,
                });
                if (access.isCurrent(access.identity)) {
                  toast.success('Invoice activity unlinked; original record retained');
                  onUnlinked();
                }
              } catch (reason) {
                if (access.isCurrent(access.identity)) {
                  const message =
                    reason instanceof Error
                      ? reason.message
                      : 'The invoice link could not be removed.';
                  setError(message);
                  toast.error(message);
                }
              } finally {
                if (access.isCurrent(access.identity)) setPending(false);
              }
            }}
          >
            Unlink from invoice
          </Button>
        ) : null}
      </footer>
      {error ? (
        <p role="alert" className="text-destructive text-xs">
          {error}
        </p>
      ) : null}
    </>
  );
}

function ActivityPicker({
  invoiceId,
  direction,
  onLinked,
}: {
  invoiceId: string;
  direction: InvoiceDirection;
  onLinked: () => void;
}) {
  const email = useSessionScopeFence(PERMISSIONS.INBOX_THREADS_READ);
  const messages = useSessionScopeFence(PERMISSIONS.INBOX_MESSAGES_READ);
  const calls = useSessionScopeFence(PERMISSIONS.CRM_INTERACTIONS_READ);
  const notes = useSessionScopeFence(PERMISSIONS.CRM_CONTACT_NOTES_READ);
  const allowed = (
    [
      ['email', 'Email conversation', email.enabled && messages.enabled],
      ['call', 'Recorded phone call', calls.enabled],
      ['note', 'Contact note', notes.enabled],
    ] as const
  ).filter((item) => item[2]);
  return (
    <>
      <DialogHeader>
        <DialogTitle>Link recorded activity</DialogTitle>
        <DialogDescription>
          Select an existing source explicitly. Linking keeps the original content and does not send
          an email or place a call.
        </DialogDescription>
      </DialogHeader>
      {allowed.length ? (
        <ActivityPickerContent
          invoiceId={invoiceId}
          direction={direction}
          allowed={allowed.map(([id, label]) => ({ id, label }))}
          onLinked={onLinked}
        />
      ) : (
        <p className="text-muted-foreground text-sm">
          Your current access does not include email conversations, recorded calls or contact notes.
        </p>
      )}
    </>
  );
}
function ActivityPickerContent({
  invoiceId,
  direction,
  allowed,
  onLinked,
}: {
  invoiceId: string;
  direction: InvoiceDirection;
  allowed: Array<{ id: InvoiceActivitySource; label: string }>;
  onLinked: () => void;
}) {
  const [kind, setKind] = useState<InvoiceActivitySource>(allowed[0]!.id);
  const [search, setSearch] = useState('');
  const [submittedSearch, setSubmittedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pending, setPending] = useState('');
  const [error, setError] = useState('');
  const access = useSessionScopeFence(manageGrant);
  const query = useFinanceInvoiceActivityOptions(direction, invoiceId, kind, submittedSearch, page);
  const current = !query.isFetching && !query.isError ? query.data : undefined;
  return (
    <div className="invoice-evidence-picker__content">
      <label className="text-xs">
        Recorded source
        <select
          className="border-input bg-background mt-1 h-8 w-full rounded-md border px-2 text-xs"
          value={kind}
          onChange={(event) => {
            setKind(event.target.value as InvoiceActivitySource);
            setPage(1);
            setError('');
          }}
        >
          {allowed.map((item) => (
            <option key={item.id} value={item.id}>
              {item.label}
            </option>
          ))}
        </select>
      </label>
      <form
        className="flex gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          setSubmittedSearch(search);
          setPage(1);
        }}
      >
        <Input
          aria-label="Search recorded activity"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search existing records"
        />
        <Button size="sm" variant="outline" type="submit">
          Search
        </Button>
      </form>
      <div className="invoice-evidence-picker__records">
        {query.isLoading || query.isFetching ? (
          <Skeleton className="h-40 w-full" aria-label="Loading recorded activity options" />
        ) : !current ? (
          <div role="alert" className="text-xs">
            <p>Recorded sources are unavailable with current access.</p>
            <Button size="xs" variant="outline" onClick={() => void query.refetch()}>
              Retry recorded sources
            </Button>
          </div>
        ) : !current.items.length ? (
          <p className="text-muted-foreground text-xs">
            No permitted recorded sources match this selection.
          </p>
        ) : (
          current.items.map((item) => (
            <article key={item.id}>
              <div>
                <h3>{item.title}</h3>
                <p>{item.detail}</p>
                <span>{new Date(item.recordedAt).toLocaleString()}</span>
                {item.href ? (
                  <Link
                    className="text-2xs ml-2 underline"
                    to={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View source
                  </Link>
                ) : null}
              </div>
              <Button
                size="xs"
                variant="outline"
                disabled={Boolean(pending)}
                aria-label={`Link ${item.title}`}
                onClick={async () => {
                  if (!access.enabled || !access.isCurrent(access.identity)) return;
                  setPending(item.id);
                  setError('');
                  try {
                    await financeInvoiceEvidenceApi.link(direction, invoiceId, kind, item.id, {
                      sessionOrigin: access.identity.sessionOrigin,
                      allowRefresh: false,
                    });
                    if (access.isCurrent(access.identity)) {
                      toast.success('Recorded activity linked to invoice');
                      onLinked();
                    }
                  } catch (reason) {
                    if (access.isCurrent(access.identity)) {
                      const message =
                        reason instanceof Error
                          ? reason.message
                          : 'The recorded source could not be linked.';
                      setError(message);
                      toast.error(message);
                    }
                  } finally {
                    if (access.isCurrent(access.identity)) setPending('');
                  }
                }}
              >
                {pending === item.id ? 'Linking…' : 'Link'}
              </Button>
            </article>
          ))
        )}
      </div>
      {current ? (
        <div className="invoice-evidence__paging">
          <span>
            {current.meta.total} source records · {current.meta.page}/
            {Math.max(1, current.meta.totalPages)}
          </span>
          <div>
            <Button
              size="xs"
              variant="ghost"
              aria-label="Previous activity sources"
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
            >
              Previous
            </Button>
            <Button
              size="xs"
              variant="ghost"
              aria-label="Next activity sources"
              disabled={page >= current.meta.totalPages}
              onClick={() => setPage(page + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}
      {error ? (
        <p role="alert" className="text-destructive text-xs">
          {error}
        </p>
      ) : null}
      <p className="text-2xs text-muted-foreground">
        Review the source and invoice before linking. Unlinking removes only this association; the
        source record remains.
      </p>
    </div>
  );
}
